/**
 * Utility functions for checking usage limits
 */

// TTS usage limits
const TTS_LIMITS = {
  WEEKLY_LIMIT: 1.0,
  MONTHLY_LIMIT: 4.00,
  TOTAL_LIMIT: 10.00
};

// AI Chat usage limits (OpenAI/Bedrock)
export const CHAT_LIMITS = {
  WEEKLY: 5.0,
  MONTHLY: 10.0,
};

/**
 * Check if TTS usage has exceeded limits
 * @param {Object} userProfile - User profile with spending data
 * @returns {Object} Usage status and limit info
 */
export const checkTtsUsageLimits = (userProfile) => {
  if (!userProfile?.spending) {
    return { withinLimits: true, limitType: null, currentUsage: 0, limit: 0 };
  }

  const spending = userProfile.spending;
  
  // Check weekly limit
  if (spending.tts_weekly >= TTS_LIMITS.WEEKLY_LIMIT) {
    console.warn('Weekly TTS limit exceeded:', spending.tts_weekly);
    return {
      withinLimits: false,
      limitType: 'weekly',
      currentUsage: spending.tts_weekly,
      limit: TTS_LIMITS.WEEKLY_LIMIT
    };
  }
  
  // Check monthly limit
  if (spending.tts_monthly >= TTS_LIMITS.MONTHLY_LIMIT) {
    console.warn('Monthly TTS limit exceeded:', spending.tts_monthly);
    return {
      withinLimits: false,
      limitType: 'monthly',
      currentUsage: spending.tts_monthly,
      limit: TTS_LIMITS.MONTHLY_LIMIT
    };
  }
  
  // Check total limit
  if (spending.tts_total >= TTS_LIMITS.TOTAL_LIMIT) {
    console.warn('Total TTS limit exceeded:', spending.tts_total);
    return {
      withinLimits: false,
      limitType: 'total',
      currentUsage: spending.tts_total,
      limit: TTS_LIMITS.TOTAL_LIMIT
    };
  }
  console.log('TTS usage within limits:', spending);
  return { withinLimits: true, limitType: null, currentUsage: 0, limit: 0 };
};

/**
 * Fetch user profile and check TTS limits
 * @param {string} userId - User ID
 * @param {Object} dynamoUtils - DynamoDB utility instance
 * @returns {Promise<Object>} Usage check result
 */
export const checkUserTtsLimits = async (userId, dynamoUtils) => {
  try {
    if (!userId || !dynamoUtils) {
      return { withinLimits: true, error: 'Missing parameters' };
    }
    
    const userProfile = await dynamoUtils.getUserProfile(userId);
    if (!userProfile) {
      return { withinLimits: true, error: 'User profile not found' };
    }
    
    return checkTtsUsageLimits(userProfile);
  } catch (error) {
    console.error('Error checking TTS usage limits:', error);
    return { withinLimits: true, error: error.message };
  }
};

/**
 * Check if overall chat usage has exceeded limits
 * @param {Object} userProfile - User profile with spending data
 * @returns {Object} Usage status and limit info
 */
export const checkChatUsageLimits = (userProfile) => {
  if (!userProfile?.spending) {
    return { withinLimits: true, limitType: null, currentUsage: 0, limit: 0 };
  }

  const spending = userProfile.spending;

  if (spending.weekly_spend >= CHAT_LIMITS.WEEKLY) {
    console.warn('Weekly chat limit exceeded:', spending.weekly_spend);
    return {
      withinLimits: false,
      limitType: 'weekly',
      currentUsage: spending.weekly_spend,
      limit: CHAT_LIMITS.WEEKLY
    };
  }

  if (spending.monthly_spend >= CHAT_LIMITS.MONTHLY) {
    console.warn('Monthly chat limit exceeded:', spending.monthly_spend);
    return {
      withinLimits: false,
      limitType: 'monthly',
      currentUsage: spending.monthly_spend,
      limit: CHAT_LIMITS.MONTHLY
    };
  }

  return { withinLimits: true, limitType: null, currentUsage: 0, limit: 0 };
};

/**
 * Fetch user profile and check chat limits
 * @param {string} userId - User ID
 * @param {Object} dynamoUtils - DynamoDB utility instance
 * @returns {Promise<Object>} Usage check result
 */
export const checkUserChatLimits = async (userId, dynamoUtils) => {
  try {
    if (!userId || !dynamoUtils) {
      return { withinLimits: true, error: 'Missing parameters' };
    }

    const userProfile = await dynamoUtils.getUserProfile(userId);
    if (!userProfile) {
      return { withinLimits: true, error: 'User profile not found' };
    }

    return checkChatUsageLimits(userProfile);
  } catch (error) {
    console.error('Error checking chat usage limits:', error);
    return { withinLimits: true, error: error.message };
  }
};
